import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { AxiosError } from 'axios';

import { calendarApi, CalendarError401Res } from '../api';
import { useRecoilState } from 'recoil';
import { syncErrorRootEmailAtom } from '../recoil';

interface UseSyncGoogleCalendarEventsParams {
  calendarIds: string[];
  userId: string;
  startTime: string;
  endTime: string;
  refetchCount?: number;
}

export function useSyncGoogleCalendarEvents() {
  const queryClient = useQueryClient();
  const [_, setSyncErrorRootEmail] = useRecoilState(syncErrorRootEmailAtom);

  const syncGoogleCalMutation = useMutation({
    mutationFn: async ({
      calendarIds,
      userId,
      startTime,
      endTime,
    }: UseSyncGoogleCalendarEventsParams) => {
      try {
        return await calendarApi.syncGoogleCalendarEvents(startTime, endTime, { userId, calendarIds });
      } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401) {
          const errorRootEmail = (axiosError.response?.data as CalendarError401Res)?.rootEmail;
          if (errorRootEmail) {
            setSyncErrorRootEmail(errorRootEmail);
          }
          return;
        }
        throw error;
      }
    },
    onSuccess: debounce((data, { startTime, endTime, refetchCount = 1 }) => {
      // Only invalidate queries if this is the last chunk to be processed
      if (refetchCount === 1) {
        queryClient.invalidateQueries({
          queryKey: ['lifestack.calendar.events', { startDate: startTime, endDate: endTime }],
        });
      }
    }, 200),
  });

  return {
    syncGoogleCalEvents: syncGoogleCalMutation.mutateAsync,
    ...syncGoogleCalMutation,
  };
}
